import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import {
  ADDITIONAL_BILLING_STATUS,
  ADDITIONAL_BILLING_VISIBLE_STATUS,
  getAdditionalBillingDisplayStatus,
} from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useState } from 'react';

import BillingIssueModal from '../DataTable/cells/BillingIssueModal';
import { BillingStatusConfirmDialog } from '../DataTable/cells/BillingStatusConfirmDialog';
import InvalidStatusTransitionModal from '../DataTable/cells/InvalidStatusTransitionModal';
type Props = {
  loadId: number;
  currentStatus: ADDITIONAL_BILLING_STATUS;
  displayMessage: string;
  isBillingUser: boolean;
  billingIssueDescription?: string;
  onStatusUpdated?: () => void;
};

const ADDITIONAL_BILLING_ISSUE_OPTIONS = ['Missing Pages', 'Blurry', 'Other'];

export const EditableAdditionalBillingStatusSelect: React.FC<Props> = observer(
  ({
    loadId,
    currentStatus,
    displayMessage,
    isBillingUser,
    onStatusUpdated,
  }) => {
    const classes = useStyles();
    const { rootStore } = useStores();

    const [openSelect, setOpenSelect] = useState(false);
    const [billingIssueModalOpen, setBillingIssueModalOpen] = useState(false);
    const [
      pendingStatus,
      setPendingStatus,
    ] = useState<ADDITIONAL_BILLING_STATUS | null>(null);

    const [openInvalidModal, setOpenInvalidModal] = useState(false);
    const [invalidMessage, setInvalidMessage] = useState('');

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDialogMessage, setConfirmDialogMessage] = useState<
      React.ReactNode
    >('');

    const [deniedDialogOpen, setDeniedDialogOpen] = useState(false);
    const [deniedReason, setDeniedReason] = useState('');

    const executeStatusUpdate = useCallback(
      async (
        newStatus: ADDITIONAL_BILLING_STATUS,
        data?: { deniedReason?: string; descriptions?: string[] },
      ) => {
        const result = await rootStore.dispatcherUpdateAdditionalBillingStatus(
          loadId,
          {
            status: newStatus,
            ...data,
          },
        );

        if (result && !result.ok) {
          const errorMsg =
            typeof result.errors === 'string'
              ? result.errors
              : result.errors?.status?.[0] || 'Failed to update status.';

          setInvalidMessage(errorMsg);
          setOpenInvalidModal(true);
          return;
        }

        // Trigger parent refresh callback
        if (onStatusUpdated) {
          onStatusUpdated();
        }
      },
      [loadId, onStatusUpdated, rootStore],
    );

    const handleChange = useCallback(
      async (event: React.ChangeEvent<{ value: unknown }>) => {
        event.stopPropagation();
        const newValue = event.target.value as ADDITIONAL_BILLING_STATUS;
        if (newValue === currentStatus) return;

        setOpenSelect(false);

        if (newValue === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE) {
          setPendingStatus(newValue);
          setBillingIssueModalOpen(true);
          return;
        }

        if (newValue === ADDITIONAL_BILLING_STATUS.DENIED) {
          setPendingStatus(newValue);
          setDeniedReason('');
          setDeniedDialogOpen(true);
          return;
        }

        const isPendingOrDenied =
          currentStatus === ADDITIONAL_BILLING_STATUS.PENDING ||
          currentStatus === ADDITIONAL_BILLING_STATUS.DENIED;

        if (
          isPendingOrDenied &&
          newValue === ADDITIONAL_BILLING_STATUS.INVOICED
        ) {
          setInvalidMessage(
            'Only items with "Approved" status can be moved to "Invoiced".',
          );
          setOpenInvalidModal(true);
          return;
        }

        setPendingStatus(newValue);
        setConfirmDialogMessage(
          `Are you sure you want to change status from "${getAdditionalBillingDisplayStatus(
            currentStatus,
          )}" to "${getAdditionalBillingDisplayStatus(newValue)}"?`,
        );
        setConfirmDialogOpen(true);
      },
      [currentStatus],
    );

    const handleConfirmBillingIssue = useCallback(
      async (descriptions: string[]) => {
        if (pendingStatus === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE) {
          await executeStatusUpdate(pendingStatus, { descriptions });
        }
        setBillingIssueModalOpen(false);
        setPendingStatus(null);
      },
      [pendingStatus, executeStatusUpdate],
    );

    const handleConfirmDialog = useCallback(async () => {
      if (pendingStatus) {
        await executeStatusUpdate(pendingStatus);
      }
      setConfirmDialogOpen(false);
      setPendingStatus(null);
    }, [pendingStatus, executeStatusUpdate]);

    const handleCancelDialog = useCallback(() => {
      setConfirmDialogOpen(false);
      setPendingStatus(null);
    }, []);

    const handleConfirmDenied = useCallback(async () => {
      if (pendingStatus === ADDITIONAL_BILLING_STATUS.DENIED) {
        await executeStatusUpdate(pendingStatus, { deniedReason });
      }
      setDeniedDialogOpen(false);
      setPendingStatus(null);
      setDeniedReason('');
    }, [pendingStatus, deniedReason, executeStatusUpdate]);

    const handleCancelDenied = useCallback(() => {
      setDeniedDialogOpen(false);
      setPendingStatus(null);
      setDeniedReason('');
    }, []);

    const handleOpenSelect = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOpenSelect(true);
    }, []);

    const iconComponent = useCallback(
      () => (
        <span onClick={handleOpenSelect} className={classes.dropdownIcon}>
          {openSelect ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </span>
      ),
      [classes.dropdownIcon, handleOpenSelect, openSelect],
    );

    return (
      <>
        <Select
          className={classes.selectContainer}
          input={<Input disableUnderline />}
          onChange={handleChange}
          value={currentStatus}
          open={openSelect}
          onClose={() => setOpenSelect(false)}
          IconComponent={iconComponent}
          renderValue={() => {
            const textContent = (
              <Typography variant="h3" noWrap className={classes.text}>
                {displayMessage}
              </Typography>
            );

            return textContent;
          }}
        >
          {ADDITIONAL_BILLING_VISIBLE_STATUS.map(
            (stat: ADDITIONAL_BILLING_STATUS) => (
              <MenuItem
                key={stat}
                disabled={
                  (!isBillingUser &&
                    (stat === ADDITIONAL_BILLING_STATUS.INVOICED ||
                      stat === ADDITIONAL_BILLING_STATUS.BILLING_ISSUE)) ||
                  currentStatus === ADDITIONAL_BILLING_STATUS.INVOICED
                }
                value={stat}
              >
                <span>{getAdditionalBillingDisplayStatus(stat)}</span>
              </MenuItem>
            ),
          )}
        </Select>

        {billingIssueModalOpen && (
          <BillingIssueModal
            loading={false}
            options={ADDITIONAL_BILLING_ISSUE_OPTIONS}
            onConfirm={handleConfirmBillingIssue}
            onCancel={() => {
              setBillingIssueModalOpen(false);
              setPendingStatus(null);
            }}
          />
        )}

        <BillingStatusConfirmDialog
          open={confirmDialogOpen}
          title="Confirm Status Change"
          message={confirmDialogMessage}
          showConfirmBtn
          onConfirm={handleConfirmDialog}
          onCancel={handleCancelDialog}
        />

        <Dialog
          open={deniedDialogOpen}
          onClose={handleCancelDenied}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle className={classes.dialogTitle}>
            Reason for Denial
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Reason / Person who denied"
              fullWidth
              variant="outlined"
              value={deniedReason}
              onChange={e => setDeniedReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button
              onClick={handleCancelDenied}
              className={classes.cancelBtn}
              disableElevation
            >
              CANCEL
            </Button>
            <Button
              onClick={handleConfirmDenied}
              className={classes.confirmBtn}
              disableElevation
              disabled={!deniedReason.trim()}
            >
              CONFIRM
            </Button>
          </DialogActions>
        </Dialog>

        <InvalidStatusTransitionModal
          open={openInvalidModal}
          message={invalidMessage}
          onClose={() => {
            setOpenInvalidModal(false);
            setInvalidMessage('');
          }}
        />
      </>
    );
  },
);

const useStyles = makeStyles(theme => ({
  selectContainer: {
    cursor: 'pointer',
    color: 'inherit',
    width: '100%',
    '& .MuiSelect-select.MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent !important',
      paddingRight: '24px !important',
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: 'transparent !important',
    },
  },
  dropdownIcon: { color: 'inherit', display: 'flex', alignItems: 'center' },
  text: { textAlign: 'center', textTransform: 'uppercase', color: 'inherit' },
  dialogTitle: {
    textAlign: 'center',
    '& h2': {
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '1.1rem',
      color: '#424242',
    },
  },
  dialogActions: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(3),
    gap: theme.spacing(2),
  },
  cancelBtn: {
    backgroundColor: '#ef5350',
    color: '#ffffff',
    fontWeight: 700,
    padding: '8px 28px',
    borderRadius: 6,
    '&:hover': {
      backgroundColor: '#e53935',
    },
  },
  confirmBtn: {
    backgroundColor: '#4884a5',
    color: '#ffffff',
    fontWeight: 700,
    padding: '8px 28px',
    borderRadius: 6,
    '&:hover': {
      backgroundColor: '#396d8a',
    },
    '&.Mui-disabled': {
      backgroundColor: '#a3c2d4',
      color: '#ffffff',
    },
  },
}));
